"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui";
import { Input } from "@repo/ui";
import { useCommandBar, type CommandBarPage } from "@/hooks/use-command-bar";
import { getShortcutKeys, KEYBOARD_SHORTCUTS, type Shortcut } from "@/lib/keyboard-shortcuts";
import { useEntitiesFromDb } from "@/hooks/schema/use-entities-from-db";
import { useCommandBarPages } from "@/hooks/use-command-bar-pages";
import {
  useSwitchableCustomers,
  type SwitchableCustomer,
} from "@/hooks/auth/use-switchable-customers";
import { fuzzySort } from "@/lib/fuzzy-search";
import { ArrowUpRight, X } from "lucide-react";
import { useEffect, useMemo } from "react";

type CommandResult = {
  /** React key, unique across all groups. */
  key: string;
  label: string;
} & (
  | {
      kind: "link";
      /** Navigation target (subdomain-less; the proxy prefixes the subdomain). */
      href: string;
    }
  | { kind: "switch"; customer: SwitchableCustomer }
  | { kind: "page"; page: CommandBarPage; shortcut?: Shortcut }
);

const SWITCH_CUSTOMER_LABEL = "Switch customer…";
// Customers only join root search once the query is specific enough not to drown out entities.
const ROOT_CUSTOMER_MIN_QUERY = 2;
const ROOT_CUSTOMER_LIMIT = 5;

const toSwitchResult = (customer: SwitchableCustomer): CommandResult => ({
  key: `customer-${customer.appId}`,
  label: customer.name,
  kind: "switch",
  customer,
});

export function CommandBar() {
  const router = useRouter();
  const { isOpen, setIsOpen, initialPage } = useCommandBar();
  const [query, setQuery] = React.useState("");
  const { availableEntities } = useEntitiesFromDb();
  const pages = useCommandBarPages();
  const { others: customers } = useSwitchableCustomers();
  const [page, setPage] = React.useState<CommandBarPage>("root");
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);
  // Only keyboard moves scroll; hover selection must not make the list jump.
  const scrollToSelectionRef = React.useRef(false);

  // Each group is ranked independently against the query.
  const entityResults = useMemo<CommandResult[]>(
    () =>
      fuzzySort(availableEntities ?? [], query, (item) => item.name).map((item) => ({
        key: item.id,
        label: item.name,
        kind: "link" as const,
        href: `/${item.name}`,
      })),
    [availableEntities, query],
  );

  const pageResults = useMemo<CommandResult[]>(
    () =>
      fuzzySort(pages, query, (item) => item.title).map((item) => ({
        key: item.id,
        label: item.title,
        kind: "link" as const,
        href: item.href,
      })),
    [pages, query],
  );

  const customerResults = useMemo<CommandResult[]>(() => {
    if (page === "customers") {
      return fuzzySort(customers, query, (c) => c.name).map(toSwitchResult);
    }
    if (query.trim().length < ROOT_CUSTOMER_MIN_QUERY) return [];
    return fuzzySort(customers, query, (c) => c.name)
      .slice(0, ROOT_CUSTOMER_LIMIT)
      .map(toSwitchResult);
  }, [customers, page, query]);

  const actionResults = useMemo<CommandResult[]>(() => {
    if (customers.length === 0) return [];
    const actions: CommandResult[] = [
      {
        key: "switch-customer",
        label: SWITCH_CUSTOMER_LABEL,
        kind: "page",
        page: "customers",
        shortcut: KEYBOARD_SHORTCUTS["global-switch-customer"],
      },
    ];
    return fuzzySort(actions, query, (a) => a.label);
  }, [customers.length, query]);

  const groups = useMemo(
    () =>
      page === "customers"
        ? [{ title: null, results: customerResults }]
        : [
            { title: "Entities", results: entityResults },
            { title: "Settings / Pages", results: pageResults },
            { title: "Customers", results: customerResults },
            { title: "Actions", results: actionResults },
          ],
    [page, entityResults, pageResults, customerResults, actionResults],
  );

  // Flat, ordered list used for keyboard navigation + selection; matches render order.
  const flatResults = useMemo(() => groups.flatMap((g) => g.results), [groups]);

  const openPage = React.useCallback((next: CommandBarPage) => {
    setPage(next);
    setQuery("");
  }, []);

  const handleSelect = React.useCallback(
    (result: CommandResult) => {
      if (result.kind === "page") {
        openPage(result.page);
        return;
      }
      if (result.kind === "switch") {
        const { switchHref, opensNewTab } = result.customer;
        if (opensNewTab) window.open(switchHref, "_blank", "noopener");
        else window.location.assign(switchHref);
        setIsOpen(false);
        return;
      }
      router.push(result.href);
      setIsOpen(false);
    },
    [openPage, router, setIsOpen],
  );

  // Reset query and selection when the dialog opens. Resetting on close would
  // change the search term while the dialog animates out, triggering a refetch
  // that flickers the results (and the dialog) right as you select an item.
  React.useEffect(() => {
    if (isOpen) {
      setQuery("");
      setPage(initialPage);
      setSelectedIndex(0);
    }
  }, [isOpen, initialPage]);

  // Handle keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        scrollToSelectionRef.current = true;
        setSelectedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        scrollToSelectionRef.current = true;
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : flatResults.length - 1));
      } else if (e.key === "Enter" && flatResults.length > 0) {
        e.preventDefault();
        handleSelect(flatResults[selectedIndex]);
      } else if (e.key === "Backspace" && page !== "root" && query === "") {
        e.preventDefault();
        openPage("root");
      }
    },
    [flatResults, selectedIndex, handleSelect, page, query, openPage],
  );

  useEffect(() => {
    if (!scrollToSelectionRef.current) return;
    scrollToSelectionRef.current = false;
    // Scroll the list itself: scrollIntoView would also scroll the dialog and hide the search row.
    const list = listRef.current;
    const row = list?.querySelector(`[data-index="${selectedIndex}"]`);
    if (!list || !row) return;
    if (selectedIndex === 0) {
      list.scrollTop = 0;
      return;
    }
    if (selectedIndex === flatResults.length - 1) {
      list.scrollTop = list.scrollHeight;
      return;
    }
    // Same 8px breathing room the list has above its first and below its last row.
    const inset = 8;
    const listBox = list.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();
    if (rowBox.top < listBox.top + inset) list.scrollTop -= listBox.top + inset - rowBox.top;
    else if (rowBox.bottom > listBox.bottom - inset)
      list.scrollTop += rowBox.bottom - (listBox.bottom - inset);
  }, [selectedIndex, flatResults.length]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [flatResults]);

  const rowClassName = (index: number) =>
    `flex w-full cursor-pointer items-center rounded-md px-2.5 py-2 text-left text-sm ${
      index === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
    }`;

  const renderRow = (result: CommandResult, index: number) => {
    const onMouseMove = () => setSelectedIndex(index);
    if (result.kind === "link") {
      return (
        <Link
          key={result.key}
          href={result.href}
          data-index={index}
          className={rowClassName(index)}
          onMouseMove={onMouseMove}
          onClick={(e) => {
            if (!e.metaKey && !e.ctrlKey) setIsOpen(false);
          }}
        >
          {result.label}
        </Link>
      );
    }
    if (result.kind === "switch") {
      const { switchHref, opensNewTab } = result.customer;
      return (
        <a
          key={result.key}
          href={switchHref}
          {...(opensNewTab ? { target: "_blank", rel: "noopener" } : {})}
          data-index={index}
          className={rowClassName(index)}
          onMouseMove={onMouseMove}
          onClick={() => setIsOpen(false)}
        >
          <span className="truncate">{result.label}</span>
          {opensNewTab && (
            <ArrowUpRight
              className="ml-auto h-4 w-4 shrink-0 text-muted-foreground"
              aria-label="Opens in a new tab"
            />
          )}
        </a>
      );
    }
    return (
      <button
        key={result.key}
        type="button"
        data-index={index}
        className={rowClassName(index)}
        onMouseMove={onMouseMove}
        onClick={() => handleSelect(result)}
      >
        {result.label}
        {result.shortcut && <ShortcutHint shortcut={result.shortcut} />}
      </button>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        // The close button is DialogContent's last child; centre it on the 60px search row.
        className="mb-auto mt-[18vh] flex h-[400px] max-w-[640px] flex-col gap-0 overflow-hidden p-0 shadow-lg [&>button:last-child]:right-5 [&>button:last-child]:top-[22px]"
        onEscapeKeyDown={(e) => {
          if (page !== "root") {
            e.preventDefault();
            openPage("root");
          }
        }}
      >
        <DialogTitle className="sr-only">Search</DialogTitle>

        <div className="flex min-h-0 w-full flex-1 flex-col items-start">
          <div className="flex w-full flex-shrink-0 items-center gap-3 border-b py-2 pl-5 pr-12">
            {page === "customers" && (
              <button
                type="button"
                onClick={() => openPage("root")}
                className="flex shrink-0 items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                aria-label="Back to all commands"
              >
                Switch customer
                <X className="h-3 w-3" />
              </button>
            )}
            <Input
              key={page}
              placeholder={
                page === "customers" ? "Search customers…" : "Search entities, settings, pages..."
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex h-11 w-full rounded-md border-none bg-transparent px-0 py-3 text-sm shadow-none outline-none placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              autoFocus
            />
          </div>

          {/* pl-2.5 mirrors the 10px scrollbar gutter so the row highlight sits symmetrically. */}
          <div
            ref={listRef}
            className="min-h-0 w-full grow overflow-y-auto pl-2.5 [scrollbar-gutter:stable]"
          >
            {flatResults.length > 0 ? (
              <div className="py-2">
                {groups.map((group, gi) => {
                  if (group.results.length === 0) return null;
                  const offset = groups.slice(0, gi).reduce((n, g) => n + g.results.length, 0);
                  return (
                    <React.Fragment key={group.title ?? gi}>
                      {group.title && (
                        <div className="px-2.5 pb-1.5 pt-2 text-xs font-medium text-muted-foreground">
                          {group.title}
                        </div>
                      )}
                      {group.results.map((result, i) => renderRow(result, offset + i))}
                    </React.Fragment>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">No results found</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShortcutHint({ shortcut }: { shortcut: Shortcut }) {
  return (
    <span className="ml-auto flex gap-1">
      {getShortcutKeys(shortcut).map((key) => (
        <kbd
          key={key}
          className="rounded border bg-muted px-1.5 font-sans text-[11px] uppercase text-muted-foreground"
        >
          {key}
        </kbd>
      ))}
    </span>
  );
}
